import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { TimeEntry } from '@ms/timesheets/dist/models/time-entry';
import { useDispatch, useSelector } from 'react-redux';
import { fakeTimeEntries } from 'features/pending-timesheets/test-data/fake-timeentries';
import { PendingTimesheet } from 'features/pending-timesheets/models/pending-timesheets.model';
import renderMobile from './timesheet-edit-modal.mobile';
import renderDesktop from './timesheet-edit-modal.desktop';
import { RootState } from 'store/root-reducer';
import { setChangedTimeEntries, setTimeEntries } from 'features/pending-timesheets/redux/time-entries-slice';
import SmartComponent from 'utils/smart-component';
import { TIMESHEET_PACKAGE } from 'api/use-query-constants';
import { TypedLocalState } from 'utils/redux-hooks';
import { useTimeEntries } from 'features/pending-timesheets/hooks/time-entries.hook';
import { AppConfig } from 'features/config/config.model';
import { TimesheetLogic } from 'features/pending-timesheets/logic/timesheet.logic';
import { IOnlineApprovalTimeEntry } from 'features/pending-timesheets/models/online-approval-time-entry.model';
import { getQueryClient } from 'features/config/config';
import './timesheet-edit-modal.scss';
import { TimesheetPackage } from 'features/pending-timesheets/models/timesheet-package.model';

export interface ITimesheetEditModalProps {
    payPeriodStart: Dayjs;
    payPeriodEnd: Dayjs;
    isOpen: boolean;
    close: () => void;
    save: (selectedTimesheet: PendingTimesheet, supervisorComments: string) => void;
}

export interface ITimesheetEditModalState extends ITimesheetEditModalProps {
    payPeriodStart: Dayjs;
    payPeriodEnd: Dayjs;
    isOpen: boolean;
    close: () => void;
    save: (selectedTimesheet: PendingTimesheet, comments: string) => void;
    supervisorComments: string;
    setSupervisorComments: (comments: string) => void;
    selectedTimesheet: PendingTimesheet;
    selectedTimesheetTimeEntries: IOnlineApprovalTimeEntry[];
    error?: unknown;
    isLoading: boolean;
    showAddDelete: boolean;
    addShift: () => void;
    isSaveDisabled: boolean;
    showCommentError: boolean;
    timesheetPackage?: TimesheetPackage;
}

const useInitialState = (props: ITimesheetEditModalProps): ITimesheetEditModalState => {
    const [showCommentError, setShowCommentError]: TypedLocalState<boolean> = useState(false);

    const selectedTimesheet = useSelector(
        (state: RootState) => state.selectedTimesheet.selectedTimesheet as PendingTimesheet,
    );

    const selectedTimesheetTimeEntries = useSelector(
        (state: RootState) => state.timeEntries.selectedTimesheetTimeEntries as IOnlineApprovalTimeEntry[],
    );

    const config = useSelector((state: RootState) => state.globalElements.appConfig as AppConfig);
    const showAddDelete = TimesheetLogic.showAddDelete(selectedTimesheet.onlineApprovalDate, config);

    const url = useSelector((state: RootState) => state.pendingTimesheets.url as string);

    const { error, isLoading, data } = useTimeEntries(selectedTimesheet.timesheetId, url);
    const timesheetPackage = { ...data } as TimesheetPackage;
    const timeEntries = timesheetPackage?.onlineApprovalTimeEntries ?? [];
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (isLoading === false) {
            const timeEntriesValue = timeEntries?.length < 1 ? fakeTimeEntries : timeEntries;
            if (timesheetPackage) {
                timesheetPackage.onlineApprovalTimeEntries = timeEntriesValue;
            }
            getQueryClient().setQueryData(TIMESHEET_PACKAGE, timesheetPackage);
            dispatch(setTimeEntries(timeEntriesValue));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, timeEntries]);

    const [supervisorComments, setSupervisorComments]: TypedLocalState<string> = React.useState(
        selectedTimesheet?.supervisorComment,
    );

    const addShift = () => {
        let newDate = dayjs(selectedTimesheet.payPeriodStart).toDate();

        const thisTimesheetsEntries = selectedTimesheetTimeEntries.filter(
            (entry: IOnlineApprovalTimeEntry) => entry.timesheetId === selectedTimesheet.timesheetId,
        );

        if (thisTimesheetsEntries.length) {
            const latestEntry = thisTimesheetsEntries.reduce(
                (a: IOnlineApprovalTimeEntry, b: IOnlineApprovalTimeEntry) => {
                    return new Date(a.entryDate) > new Date(b.entryDate) ? a : b;
                },
            );
            newDate = dayjs(latestEntry.entryDate).add(1, 'days').toDate();
        }

        const newTimeEntry = new TimeEntry(selectedTimesheet.timesheetId, newDate) as IOnlineApprovalTimeEntry;
        newTimeEntry.isActive = true;
        newTimeEntry.isNew = true;

        if (
            timesheetPackage?.timesheet.showCostCenters &&
            timesheetPackage?.costCenters &&
            timesheetPackage.costCenters.length > 0
        ) {
            const defaultCostCenter = timesheetPackage.costCenters.find((cc) => cc.isDefault);
            if (defaultCostCenter) {
                newTimeEntry.costCenterId = defaultCostCenter.costCenterId;
            }
        }

        dispatch(setChangedTimeEntries(newTimeEntry));
    };

    React.useEffect(() => {
        if (
            TimesheetLogic.hasChanges(selectedTimesheetTimeEntries, selectedTimesheet, supervisorComments) &&
            TimesheetLogic.commentIsEmpty(supervisorComments)
        ) {
            setShowCommentError(true);
        } else {
            setShowCommentError(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supervisorComments, selectedTimesheetTimeEntries]);

    const isSaveDisabled =
        TimesheetLogic.isAlreadyApproved(selectedTimesheet) ||
        TimesheetLogic.commentIsEmpty(supervisorComments) ||
        !TimesheetLogic.hasChanges(selectedTimesheetTimeEntries, selectedTimesheet, supervisorComments) ||
        TimesheetLogic.hasInvalidEntries(selectedTimesheetTimeEntries);

    return {
        ...props,
        supervisorComments,
        setSupervisorComments,
        selectedTimesheet,
        selectedTimesheetTimeEntries,
        error,
        isLoading,
        showAddDelete,
        addShift,
        isSaveDisabled,
        showCommentError,
        timesheetPackage,
    };
};

export { renderDesktop as TimesheetEditModalDesktop, renderMobile as TimesheetEditModalMobile };

export default SmartComponent<ITimesheetEditModalProps, ITimesheetEditModalState>(
    useInitialState,
    renderDesktop,
    renderMobile,
);
